import PageGlow from "../components/PageGlow";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Users, Pencil, Trash2, CreditCard, Sparkles, BadgeCheck, ShieldAlert, Zap, Upload, RotateCcw, XCircle, Archive, FileUp, BarChart3, UserRound, Info, ArrowUpRight, X, Pause, Play } from "lucide-react";
import { api, formatApiError, formatSalary, uploadFile, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import JobReport from "../components/JobReport";
import CandidatePanel from "../components/CandidatePanel";
import MessagesInbox from "../components/MessagesInbox";
import ImageLightbox from "../components/ImageLightbox";
import Pager, { pageSlice } from "../components/Pager";
import NotificationPrefs from "../components/NotificationPrefs";
import AccountVerification from "../components/AccountVerification";
import EmailConfirmBanner from "../components/EmailConfirmBanner";
import EmployerInterviews from "../components/EmployerInterviews";
import EmployerIntegrations from "../components/EmployerIntegrations";
import InstallAppPrompt from "../components/InstallAppPrompt";
import { MobileMenuHome, MobileBackHeader } from "../components/MobileDashMenu";

const JOB_STATUS = {
  live: { label: "Live", cls: "bg-green-50 text-green-700" },
  paused: { label: "Paused", cls: "bg-zinc-100 text-zinc-600" },
  pending_review: { label: "In review", cls: "bg-blue-50 text-blue-700" },
  awaiting_payment: { label: "Awaiting payment", cls: "bg-amber-50 text-amber-700" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700" },
  expired: { label: "Expired", cls: "bg-zinc-100 text-zinc-500" },
  closed: { label: "Closed", cls: "bg-zinc-100 text-zinc-500" },
  archived: { label: "Archived", cls: "bg-zinc-100 text-zinc-400" },
};

const APP_STATUSES = ["new", "screening", "shortlisted", "interview", "hired", "rejected"];

const HoverTip = ({ label, testid, onEnter, children }) => (
  <span className="group relative inline-block cursor-help underline decoration-dotted decoration-zinc-300 underline-offset-2"
    onMouseEnter={onEnter} data-testid={testid}>
    {label}
    <span className="pointer-events-none absolute bottom-full left-0 z-30 mb-1.5 w-72 max-w-[calc(100vw-2rem)] rounded-md bg-zinc-950 px-3 py-2.5 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      {children}
    </span>
  </span>
);

const GOV_LINKS = [
  ["Employ someone: step by step", "https://www.gov.uk/employ-someone"],
  ["Check a job applicant's right to work", "https://www.gov.uk/check-job-applicant-right-to-work"],
  ["Equality Act 2010: guidance for employers", "https://www.gov.uk/guidance/equality-act-2010-guidance"],
  ["National Minimum Wage and Living Wage rates", "https://www.gov.uk/national-minimum-wage-rates"],
  ["Holiday entitlement", "https://www.gov.uk/holiday-entitlement-rights"],
  ["Employment agencies: the rules", "https://www.gov.uk/guidance/employment-agencies-and-employment-businesses"],
  ["ACAS: recruiting and hiring advice", "https://www.acas.org.uk/recruitment-and-induction"],
];

const AnBar = ({ rows, tid }) => (
  <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid={tid}>
    {rows.length === 0 ? <p className="text-xs text-zinc-400">No tracked views yet.</p> : (
      <div className="space-y-2">
        {rows.slice(0, 8).map((r) => (
          <div key={r.label}>
            <div className="flex justify-between text-xs mb-0.5"><span className="text-zinc-600">{r.label}</span><span className="font-semibold text-zinc-900">{r.count}</span></div>
            <div className="h-1.5 rounded-full bg-zinc-100"><div className="h-1.5 rounded-full bg-zinc-900" style={{ width: `${Math.max(4, r.count / rows[0].count * 100)}%` }} /></div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AnalyticsPanel = ({ a }) => {
  const tips = [];
  const live = a.per_job.filter((j) => j.status === "live");
  const noSalary = live.filter((j) => !j.has_salary);
  if (noSalary.length) tips.push(`${noSalary.length} live listing${noSalary.length === 1 ? "" : "s"} ${noSalary.length === 1 ? "shows" : "show"} no salary. Listings with a salary get noticeably more applications and rank better on Google Jobs. Add one from Edit.`);
  const singleLoc = live.filter((j) => j.locations <= 1);
  if (singleLoc.length) tips.push(`${singleLoc.length} live listing${singleLoc.length === 1 ? "" : "s"} appear in only one location. Location boost shows a listing in up to 10 real nearby towns and multiplies its reach.`);
  if (a.views >= 50 && a.conversion < 2) tips.push(`Your apply rate is ${a.conversion}%. Clear job titles, a salary range and a short first paragraph usually lift it.`);
  const mobile = (a.views_by_device.find((d) => d.label === "Mobile") || {}).count || 0;
  if (a.views > 0 && mobile / a.views > 0.6) tips.push("Most of your views are on phones. Put the salary and the key requirement in the first two lines.");
  const best = [...a.per_job].sort((x, y) => y.views - x.views)[0];
  if (best && best.views > 0) tips.push(`Your best performer is "${best.title}" with ${best.views} views. Reuse what works: same title style, same clarity.`);
  if (!tips.length) tips.push("Everything looks healthy. Keep listings fresh: updated titles and clear salaries keep views coming.");
  const maxDaily = Math.max(1, ...a.views_daily.map((d) => d.count));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="an-cards">
        {[
          ["Views", a.views, "how many times your listings were opened", ""],
          ["Unique visitors", a.unique_visitors, "distinct people, the same person twice counts once", ""],
          ["Applications", a.applications, "completed applications received", a.applications > 0 ? "green" : ""],
          ["Saved", a.saves, "candidates who bookmarked a listing", a.saves > 0 ? "green" : ""],
          ["Apply rate", `${a.conversion}%`, "applications per 100 views", a.views >= 20 ? (a.conversion >= 2 ? "green" : "amber") : ""],
          ["Live now", a.live_jobs, "listings currently published", a.live_jobs > 0 ? "green" : "amber"],
        ].map(([label, value, tip, tone]) => (
          <div key={label} className={`group relative border rounded-md p-4 bg-white ${
            tone === "green" ? "border-green-200" : tone === "amber" ? "border-amber-300 bg-amber-50/40" : "border-zinc-200"}`}
            data-testid={`an-card-${String(label).toLowerCase().replace(/\s/g, "-")}`}>
            <p className={`font-display text-xl font-bold ${
              tone === "green" ? "text-green-700" : tone === "amber" ? "text-amber-700" : "text-zinc-950"}`}>{value}</p>
            <p className="text-[11px] text-zinc-500 mt-1 cursor-help underline decoration-dotted decoration-zinc-300 underline-offset-2">{label}</p>
            <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-1.5 w-48 rounded-md bg-zinc-950 px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">{tip}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-400">Last {a.days} days of tracked activity. Every figure is measured from real visits and applications, nothing estimated.</p>

      <div className="border border-zinc-200 rounded-md bg-white overflow-x-auto" data-testid="an-jobs-table">
        <p className="px-4 py-3 font-display font-bold text-zinc-950 border-b border-zinc-100">Performance by listing</p>
        {a.per_job.length === 0 ? (
          <p className="p-8 text-sm text-zinc-500 text-center">No listings yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-zinc-200 bg-zinc-50 text-left">
              <th className="px-4 py-2.5 font-semibold text-zinc-600">Listing</th>
              <th className="px-4 py-2.5 font-semibold text-zinc-600">Status</th>
              <th className="px-4 py-2.5 font-semibold text-zinc-600 text-right">Views</th>
              <th className="px-4 py-2.5 font-semibold text-zinc-600 text-right">Applicants</th>
              <th className="px-4 py-2.5 font-semibold text-zinc-600 text-right">Apply rate</th>
              <th className="px-4 py-2.5 font-semibold text-zinc-600 text-right">Locations</th>
              <th className="px-4 py-2.5 font-semibold text-zinc-600">Salary</th>
            </tr></thead>
            <tbody>
              {a.per_job.map((j) => (
                <tr key={j.id} className="border-b border-zinc-100 last:border-0" data-testid={`an-job-${j.slug}`}>
                  <td className="px-4 py-3"><p className="font-medium text-zinc-900">{j.title}</p><p className="text-[11px] text-zinc-400">{j.location}</p></td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${j.status === "live" ? "bg-green-50 text-green-700" : j.status === "pending_review" ? "bg-blue-50 text-blue-700" : "bg-zinc-100 text-zinc-500"}`}>{j.status.replace(/_/g, " ")}</span></td>
                  <td className="px-4 py-3 text-right font-medium text-zinc-900">{j.views}</td>
                  <td className="px-4 py-3 text-right font-medium text-zinc-900">{j.applications}</td>
                  <td className="px-4 py-3 text-right text-zinc-600">{j.views ? `${(j.applications / j.views * 100).toFixed(1)}%` : "-"}</td>
                  <td className="px-4 py-3 text-right text-zinc-600">{j.locations}</td>
                  <td className="px-4 py-3">{j.has_salary ? <span className="text-xs text-green-700 font-semibold">Shown</span> : <span className="text-xs text-amber-700 font-semibold">Missing</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div><p className="font-display font-bold text-zinc-950 mb-2">Where viewers are</p><AnBar rows={a.views_by_location} tid="an-locations" /></div>
        <div><p className="font-display font-bold text-zinc-950 mb-2">How they found you</p><AnBar rows={a.views_by_source} tid="an-sources" /></div>
        <div><p className="font-display font-bold text-zinc-950 mb-2">Devices</p><AnBar rows={a.views_by_device} tid="an-devices" /></div>
      </div>

      <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid="an-daily">
        <p className="font-display font-bold text-zinc-950 mb-3">Views per day</p>
        {a.views_daily.every((d) => d.count === 0) ? (
          <p className="text-xs text-zinc-400">No tracked views in this period yet.</p>
        ) : (
          <div className="flex items-end gap-[3px] h-24">
            {a.views_daily.map((d) => (
              <div key={d.date} className="flex-1 bg-zinc-900/85 rounded-t-sm hover:bg-blue-700 transition-colors duration-150"
                title={`${new Date(d.date + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}: ${d.count} view${d.count === 1 ? "" : "s"}`}
                style={{ height: `${Math.max(3, d.count / maxDaily * 100)}%` }} />
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid="an-tips">
          <p className="font-display font-bold text-zinc-950 mb-2">Tips from your numbers</p>
          <ul className="space-y-2">
            {tips.map((t, i) => <li key={i} className="text-sm text-zinc-600 leading-relaxed flex gap-2" data-testid={`an-tip-${i}`}><span className="text-blue-700 font-bold shrink-0">→</span>{t}</li>)}
          </ul>
        </div>
        <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid="an-links">
          <p className="font-display font-bold text-zinc-950 mb-2">Useful links, UK employment guidance</p>
          <ul className="space-y-1.5">
            {GOV_LINKS.map(([label, url], i) => (
              <li key={i}>
                <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`an-link-${i}`}
                  className="text-sm text-blue-700 underline underline-offset-2 hover:text-blue-900">{label}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const fmtWhen = (at) => at ? new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
const fmtTime = (at) => at ? new Date(at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const ViewsTip = ({ data }) => {
  if (!data) return "Loading…";
  if (!data.views.length && !data.signed_in_viewers.length) return "No views yet, once jobseekers open this listing, their times, sources and locations appear here.";
  return (
    <>
      {data.signed_in_viewers.length > 0 && (
        <span className="block mb-1.5">
          <span className="font-semibold">Signed-in viewers:</span>
          {data.signed_in_viewers.slice(0, 4).map((v, i) => (
            <span key={i} className="block">{v.name}{v.location ? ` · ${v.location}` : ""}{v.at ? ` · ${fmtTime(v.at)}` : ""}</span>
          ))}
          {data.signed_in_viewers.length > 4 && <span className="block text-zinc-400">+{data.signed_in_viewers.length - 4} more</span>}
        </span>
      )}
      {data.views.length > 0 && (
        <span className="block">
          <span className="font-semibold">Recent views ({data.views.length}):</span>
          {data.views.slice(0, 6).map((v, i) => (
            <span key={i} className="block">
              {fmtTime(v.at)}{[v.city, v.country].filter(Boolean).join(", ") ? ` · ${[v.city, v.country].filter(Boolean).join(", ")}` : ""} · {v.source || "Direct"}{v.device ? ` · ${v.device}` : ""}
            </span>
          ))}
          {data.views.length > 6 && <span className="block mt-1 text-zinc-400">+{data.views.length - 6} more views</span>}
        </span>
      )}
    </>
  );
};

const ApplicantsTip = ({ data }) => {
  if (!data) return "Loading…";
  if (!data.applicants.length) return "No applicants yet, names and locations appear here as people apply.";
  return (
    <>
      {data.applicants.slice(0, 6).map((a, i) => (
        <span key={i} className="block">
          <span className="font-semibold">{a.name || "Applicant"}</span>
          {a.location ? ` · ${a.location}` : ""}{a.at ? ` · ${fmtWhen(a.at)}` : ""}
        </span>
      ))}
      {data.applicants.length > 6 && <span className="block mt-1 text-zinc-400">+{data.applicants.length - 6} more, see the Applicants list</span>}
    </>
  );
};

const LocationsTip = ({ data }) => {
  if (!data) return "Loading…";
  const locs = data.locations || [];
  if (!locs.length) return "This listing is posted to its main location only.";
  return (
    <>
      <span className="font-semibold">Posted to {locs.length} location{locs.length === 1 ? "" : "s"}:</span>
      <span className="block mt-1">{locs.join(" · ")}</span>
    </>
  );
};

export default function EmployerDashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const liveJobs = useMemo(() => jobs.filter((j) => j.status === "live"), [jobs]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicantsFor, setApplicantsFor] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [insights, setInsights] = useState({});
  const loadInsights = (jobId) => {
    if (insights[jobId]) return;
    api.get(`/employer/jobs/${jobId}/insights`)
      .then((r) => setInsights((m) => ({ ...m, [jobId]: r.data })))
      .catch(() => {});
  };
  const [analytics, setAnalytics] = useState(null);
  const [reportJob, setReportJob] = useState(null);
  const [panelAppId, setPanelAppId] = useState(null);
  const [loadingApps, setLoadingApps] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editBoostInput, setEditBoostInput] = useState("");
  const [editBoostLoading, setEditBoostLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState(null);
  const [companySaving, setCompanySaving] = useState(false);
  const [profiles, setProfiles] = useState({});
  const [loadingProfile, setLoadingProfile] = useState("");
  const [verifyForm, setVerifyForm] = useState({ company_number: "", registered_address: "", director_name: "" });

  // a CH-linked company submits verification against its official record, prefill and lock
  useEffect(() => {
    if (user?.ch_locked && user?.company_number) {
      setVerifyForm((v) => ({ ...v, company_number: user.company_number, registered_address: user.company_address || "" }));
    }
  }, [user?.ch_locked, user?.company_number, user?.company_address]);
  const [verifyDocs, setVerifyDocs] = useState([]);
  const [verifyUploading, setVerifyUploading] = useState(false);
  const [verifySaving, setVerifySaving] = useState(false);
  const [upgradeJob, setUpgradeJob] = useState(null);
  const [upgradePromoCode, setUpgradePromoCode] = useState("");
  const [upgradePromo, setUpgradePromo] = useState(null);
  const [upgradePromoError, setUpgradePromoError] = useState("");

  const applyUpgradePromo = async () => {
    if (!upgradePromoCode.trim()) return;
    setUpgradePromoError("");
    setUpgradePromo(null);
    try {
      const r = await api.post("/payments/validate-promo", { code: upgradePromoCode.trim() });
      if (r.data.valid) setUpgradePromo({ code: r.data.code, percent: r.data.percent });
      else setUpgradePromoError(r.data.reason || "That code isn't valid");
    } catch (err) {
      setUpgradePromoError(formatApiError(err, "Could not check that code"));
    }
  };
  const [logoUploading, setLogoUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [team, setTeam] = useState([]);
  const [teamForm, setTeamForm] = useState({ name: "", email: "", role: "recruiter" });
  const [teamBusy, setTeamBusy] = useState(false);
  const isTeamMember = !!user?.parent_employer_id;

  const loadTeam = useCallback(() => {
    if (user?.role === "employer") {
      api.get("/employer/team").then((r) => setTeam(r.data)).catch(() => {});
    }
  }, [user]);

  const inviteMember = async (e) => {
    e.preventDefault();
    setTeamBusy(true);
    try {
      const r = await api.post("/employer/team", { name: teamForm.name, email: teamForm.email, team_role: teamForm.role });
      if (r.data?.email_sent === false) {
        toast.warning("Account created. The email service is busy, so the invite is queued and will send automatically.");
      } else {
        toast.success(`Invite sent to ${teamForm.email}`);
      }
      setTeamForm({ name: "", email: "", role: "recruiter" });
      loadTeam();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTeamBusy(false);
    }
  };

  const removeMember = async (id) => {
    try {
      await api.delete(`/employer/team/${id}`);
      toast.success("Team member removed");
      loadTeam();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setMemberRole = async (id, role) => {
    try {
      await api.patch(`/employer/team/${id}`, { team_role: role });
      toast.success("Role updated");
      loadTeam();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [tab, setTab] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? "menu" : "overview"));
  useEffect(() => { if (tab === "analytics") api.get("/employer/analytics").then((r) => setAnalytics(r.data)).catch(() => {}); }, [tab]);
  const refreshAnalytics = () => {
    setAnalytics(null);
    api.get("/employer/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  const [msgUnread, setMsgUnread] = useState(0);
  const [chatOpenPref, setChatOpenPref] = useState(null);
  useEffect(() => {
    const load = () => api.get("/messages/unread-count").then((r) => setMsgUnread(r.data.unread || 0)).catch(() => {});
    const onRead = (e) => setMsgUnread((n) => Math.max(0, n - (e.detail?.cleared || 0)));
    window.addEventListener("ht:msg-read", onRead);
    load();
    const t = setInterval(load, 30000);
    return () => { clearInterval(t); window.removeEventListener("ht:msg-read", onRead); };
  }, []);
  const chatOpen = chatOpenPref ?? (user?.candidate_chat_open !== false);
  const toggleCandidateChat = async () => {
    const v = !chatOpen;
    setChatOpenPref(v);
    try {
      await api.put("/auth/profile", { candidate_chat_open: v });
      toast.success(v ? "Candidates can now start a conversation after applying" : "Only your team can start conversations now");
    } catch (err) {
      setChatOpenPref(!v);
      toast.error(formatApiError(err));
    }
  };
  const [sub, setSub] = useState(null);
  const [cvViewer, setCvViewer] = useState(null);
  const [photoViewer, setPhotoViewer] = useState(null);
  const [subInvoices, setSubInvoices] = useState([]);
  const [subBusy, setSubBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelForm, setCancelForm] = useState({ reason: "", comment: "" });
  const [retentionOffer, setRetentionOffer] = useState(null);
  const [topup, setTopup] = useState({ credits: 0, urgent: 0 });
  const [topupBusy, setTopupBusy] = useState(false);
  const [urgentPrice, setUrgentPrice] = useState(9);
  const [perf, setPerf] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candSearch, setCandSearch] = useState("");
  const [candJob, setCandJob] = useState("");
  const [candStatus, setCandStatus] = useState("");
  const [talent, setTalent] = useState([]);
  const [talentError, setTalentError] = useState("");
  const [talentLoading, setTalentLoading] = useState(false);
  const [talentRequests, setTalentRequests] = useState([]);
  const [teamFilter, setTeamFilter] = useState({ role: "all", status: "all" });
  const filteredTeam = useMemo(() => team.filter((m) =>
    (teamFilter.role === "all" || (m.is_owner ? "owner" : (m.team_role || "recruiter")) === teamFilter.role) &&
    (teamFilter.status === "all" || (teamFilter.status === "active") === !!m.is_active)
  ), [team, teamFilter]);
  const [matchMode, setMatchMode] = useState(false);
  const [listingSearch, setListingSearch] = useState("");
  const [listingStatus, setListingStatus] = useState("");
  const [listingSort, setListingSort] = useState("newest");
  const [listingPage, setListingPage] = useState(1);
  const [candPage, setCandPage] = useState(1);
  const [talentPage, setTalentPage] = useState(1);
  const [appPage, setAppPage] = useState(1);
  useEffect(() => { setAppPage(1); }, [applicantsFor]);
  const [talentSearch, setTalentSearch] = useState("");
  const [talentLoc, setTalentLoc] = useState("");
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteJob, setInviteJob] = useState("");

  useEffect(() => {
    if (tab !== "talent" || matchMode) return;
    setTalentLoading(true);
    setTalentError("");
    const t = setTimeout(() => {
      api.get("/employer/talent", { params: { search: talentSearch, location: talentLoc } })
        .then((r) => setTalent(r.data))
        .catch((err) => {
          setTalent([]);
          setTalentError(err?.response?.data?.detail || "Could not load the talent pool");
        })
        .finally(() => setTalentLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [tab, talentSearch, talentLoc, matchMode]);

  useEffect(() => {
    if (tab === "talent") {
      api.get("/employer/talent/requests").then((r) => setTalentRequests(r.data)).catch(() => {});
    }
  }, [tab]);

  const requestAccess = async (p) => {
    try {
      await api.post(`/employer/talent/${p.id}/request-access`, { message: "" });
      toast.success(`Request sent to ${p.name}. They choose whether to share their details.`);
      const r = await api.get("/employer/talent/requests");
      setTalentRequests(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runTalentMatch = async () => {
    setTalentLoading(true);
    setTalentError("");
    try {
      const r = await api.get("/employer/talent/match");
      setTalent(r.data);
      setMatchMode(true);
      if (r.data.length === 0) toast.info("No strong matches yet. As candidates opt in, they will appear here.");
    } catch (err) {
      setTalent([]);
      setTalentError(err?.response?.data?.detail || "Could not match right now");
    } finally {
      setTalentLoading(false);
    }
  };

  const sendTalentInvite = async () => {
    if (!inviteFor || !inviteJob) return;
    try {
      await api.post(`/employer/talent/${inviteFor.id}/invite`, { job_id: inviteJob });
      toast.success(`Invite sent to ${inviteFor.name}`);
      setInviteFor(null);
      setInviteJob("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (tab === "overview") {
      api.get("/employer/analytics?days=30").then((r) => setPerf(r.data)).catch(() => {});
    }
  }, [tab]);

  useEffect(() => {
    if (tab !== "candidates") return;
    setCandLoading(true);
    const t = setTimeout(() => {
      api.get("/employer/candidates", { params: { search: candSearch, status: candStatus, job_id: candJob } })
        .then((r) => setCandidates(r.data))
        .catch(() => {})
        .finally(() => setCandLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [tab, candSearch, candStatus, candJob]);

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "listings", label: "Listings" },
    { id: "analytics", label: "Analytics" },
    { id: "candidates", label: "Candidates" },
    { id: "messages", label: msgUnread > 0 ? (<>Messages <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white" data-testid="emp-tab-msg-badge">{msgUnread}</span></>) : "Messages" },
    { id: "interviews", label: "Interviews" },
    { id: "talent", label: "Talent pool" },
    { id: "settings", label: "Settings" },
  ];

  const loadSub = useCallback(() => {
    api.get("/subscriptions/my").then((r) => {
      setSub(r.data.subscription);
      setSubInvoices(r.data.invoices || []);
      setRetentionOffer(r.data.subscription?.retention_offer || null);
    }).catch(() => {});
    api.get("/pricing").then((r) => {
      const u = r.data?.urgent;
      if (u) setUrgentPrice(u.price ?? (u.amount || 900) / 100);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadSub(); }, [loadSub]);
  const cancelSub = async (acceptRetention = false) => {
    setSubBusy(true);
    try {
      const r = await api.post("/subscriptions/cancel", {
        reason: cancelForm.reason, comment: cancelForm.comment, accept_retention: acceptRetention,
      });
      setSub(r.data.subscription);
      if (r.data.retention_applied) {
        toast.success("Done, your discount is applied and your plan carries on as normal.");
      } else {
        toast.success("Your plan stays active until the renewal date, then ends.");
      }
      setCancelOpen(false);
      setCancelForm({ reason: "", comment: "" });
      setRetentionOffer(null);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubBusy(false); }
  };
  const resumeSub = async () => {
    setSubBusy(true);
    try {
      const r = await api.post("/subscriptions/resume");
      setSub(r.data.subscription);
      toast.success("Your plan continues as normal.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubBusy(false); }
  };
  const acceptOffer = async () => {
    setSubBusy(true);
    try {
      const r = await api.post("/subscriptions/offer/accept", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(formatApiError(err)); setSubBusy(false); }
  };


  const buyTopup = async () => {
    setTopupBusy(true);
    try {
      const r = await api.post("/subscriptions/topup", {
        credits: Number(topup.credits) || 0, urgent_credits: Number(topup.urgent) || 0,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(formatApiError(err)); setTopupBusy(false); }
  };

  const archiveJob = async (job) => {
    try {
      await api.patch(`/employer/jobs/${job.id}/status`, { status: "archived" });
      toast.success("Archived. Tick 'Show archived' to see it again");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const repostJob = (job) => {
    navigate("/post-a-job", { state: { prefill: job } });
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const r = await uploadFile(file, "photo");
      setCompanyForm((f) => ({ ...f, company_photos: [...(f.company_photos || []), r.path].slice(0, 6) }));
      toast.success("Photo added. Save the profile to apply it");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const toggleClose = async (job) => {
    const action = job.status === "live" ? "closed" : "reopen";
    try {
      await api.patch(`/employer/jobs/${job.id}/status`, { status: action });
      toast.success(action === "closed" ? "Job closed. It's no longer public" : "Sent back for review");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePause = async (job) => {
    const action = job.status === "live" ? "paused" : "resumed";
    try {
      await api.patch(`/employer/jobs/${job.id}/status`, { status: action });
      toast.success(action === "paused"
        ? "Job paused. Hidden from search and applications — everything is kept."
        : "Job is live again");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };


  const doUpgrade = async (job, targetTier, urgent) => {
    try {
      const res = await api.post("/payments/upgrade", { job_id: job.id, target_tier: targetTier || undefined, urgent, origin_url: window.location.origin, promo_code: upgradePromo ? upgradePromo.code : "" });
      if (res.data.free) {
        toast.success("Upgrade applied. No payment needed");
        setUpgradeJob(null);
        load();
        return;
      }
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadVerifyDoc = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVerifyUploading(true);
    try {
      const r = await uploadFile(file, kind);
      setVerifyDocs((d) => [...d, { path: r.path, filename: r.filename, kind }]);
      toast.success(`${file.name} attached`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setVerifyUploading(false);
      e.target.value = "";
    }
  };

  const submitVerification = async (e) => {
    e.preventDefault();
    setVerifySaving(true);
    try {
      await api.post("/verification/submit", { ...verifyForm, documents: verifyDocs });
      await refreshUser();
      toast.success("Submitted. We'll review it as soon as possible and email you the outcome.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setVerifySaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const r = await uploadFile(file, "logo");
      setCompanyForm((f) => ({ ...f, company_logo: r.path }));
      toast.success("Logo uploaded. Save the profile to apply it");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const openCompanyEditor = () => {
    setCompanyForm({
      company_name: user?.company_name || "",
      company_website: user?.company_website || "",
      company_industry: user?.company_industry || "",
      company_size: user?.company_size || "",
      company_description: user?.company_description || "",
      company_logo: user?.company_logo || "",
      company_address: user?.company_address || "",
      company_reg_number: user?.company_reg_number || "",
      trading_name: user?.trading_name || "",
      phone: user?.phone || "",
      company_photos: user?.company_photos || [],
      brand_color: user?.brand_color || "",
      brand_tagline: user?.brand_tagline || "",
    });
    setCompanyOpen(true);
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    setCompanySaving(true);
    try {
      await api.put("/auth/profile", companyForm);
      await refreshUser();
      toast.success("Company profile saved");
      setCompanyOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCompanySaving(false);
    }
  };

  const toggleProfile = async (appId) => {
    if (profiles[appId]) {
      setProfiles((p) => ({ ...p, [appId]: null }));
      return;
    }
    setLoadingProfile(appId);
    try {
      const res = await api.get(`/employer/applications/${appId}/candidate`);
      setProfiles((p) => ({ ...p, [appId]: res.data }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingProfile("");
    }
  };


  const load = useCallback(() => {
    api.get("/employer/jobs", { params: showArchived ? { include_archived: 1 } : {} })
      .then((res) => setJobs(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTeam(); }, [loadTeam]);
  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);

  const openApplicants = async (job) => {
    setApplicantsFor(job);
    setApplicants([]);
    setLoadingApps(true);
    try {
      const res = await api.get(`/employer/jobs/${job.id}/applications`);
      setApplicants(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingApps(false);
    }
  };

  const setAppStatus = async (appId, status) => {
    try {
      await api.patch(`/employer/applications/${appId}`, { status });
      setApplicants((list) => list.map((a) => (a.id === appId ? { ...a, status } : a)));
      toast.success("Application updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEdit = (job) => {
    setEditJob(job);
    setEditForm({
      title: job.title, category: job.category, location: job.location,
      workplace: job.workplace, job_type: job.job_type,
      salary_min: job.salary_min ?? "", salary_max: job.salary_max ?? "",
      salary_period: job.salary_period, show_salary: job.show_salary,
      description: job.description, company_name: job.company_name,
      company_website: job.company_website || "", company_description: job.company_description || "",
      application_email: job.application_email || "", tier: job.tier,
      partner_distribution: !!job.partner_distribution,
      boost_locations: job.boost_locations || [],
    });
    setEditBoostInput("");
  };

  const suggestEditBoost = async () => {
    if (!editForm?.location?.trim()) return;
    setEditBoostLoading(true);
    try {
      const r = await api.get("/employer/location-boost", { params: { location: editForm.location.trim() } });
      const sugg = r.data.suggestions || r.data || [];
      if (!sugg.length) toast.info("No nearby areas found for that location, add them yourself below.");
      setEditForm((f) => ({ ...f, boost_locations: [...new Set([...(f.boost_locations || []), ...sugg])].slice(0, 10) }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditBoostLoading(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/employer/jobs/${editJob.id}`, {
        ...editForm,
        salary_min: editForm.salary_min === "" ? null : Number(editForm.salary_min),
        salary_max: editForm.salary_max === "" ? null : Number(editForm.salary_max),
      });
      toast.success("Listing updated");
      setEditJob(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm(`Take down "${job.title}"? This removes the listing and its applications.`)) return;
    try {
      await api.delete(`/employer/jobs/${job.id}`);
      toast.success("Listing removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const payNow = async (job) => {
    try {
      const res = await api.post("/payments/checkout", { job_id: job.id, origin_url: window.location.origin });
      if (res.data.free) {
        toast.success("Done. No payment needed. Your listing is in review");
        load();
        return;
      }
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filteredJobs = useMemo(() => {
    let list = jobs.filter((j) => showArchived || j.status !== "archived");
    const q = listingSearch.trim().toLowerCase();
    if (q) list = list.filter((j) => `${j.title || ""} ${j.location || ""}`.toLowerCase().includes(q));
    if (listingStatus) list = list.filter((j) => j.status === listingStatus);
    const sorters = {
      newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      oldest: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      views: (a, b) => (b.views || 0) - (a.views || 0),
      applicants: (a, b) => (b.applications_count || 0) - (a.applications_count || 0),
      az: (a, b) => (a.title || "").localeCompare(b.title || ""),
    };
    return [...list].sort(sorters[listingSort] || sorters.newest);
  }, [jobs, showArchived, listingSearch, listingStatus, listingSort]);

  const totals = useMemo(() => ({
    live: jobs.filter((j) => j.status === "live").length,
    views: jobs.reduce((s, j) => s + (j.views || 0), 0),
    applications: jobs.reduce((s, j) => s + (j.applications_count || 0), 0),
  }), [jobs]);

  return (
    <main className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="employer-dashboard">
      <InstallAppPrompt />
      <PageGlow subtle />
      <Seo title="Employer Dashboard | HiredToday" description="Manage your job listings and applicants." path="/dashboard" />
      <EmailConfirmBanner />
      <div className="hidden sm:flex sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">
              {user?.company_name || "Your listings"}
            </h1>
            <span className="group relative inline-flex mt-2" data-testid="dashboard-info">
              <Info className="h-4 w-4 text-zinc-400 cursor-help" />
              <span className="pointer-events-none absolute right-0 sm:right-auto sm:left-0 top-full z-20 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-md bg-zinc-950 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">Manage your roles and the people applying for them. Track views, applicants and interviews from the tabs below.</span>
            </span>
            {sub && ["active", "past_due", "cancelling"].includes(sub.status) ? (
              <button onClick={() => setTab("settings")} data-testid="plan-chip"
                title="Go to your company settings to manage plan and billing"
                className="group relative mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold border border-violet-200 bg-violet-50 text-violet-700 px-2.5 py-1 rounded-full hover:bg-violet-100 transition-colors duration-200">
                <span className={`h-1.5 w-1.5 rounded-full ${sub.status === "active" ? "bg-green-500" : "bg-amber-500"}`} />
                {sub.plan_name} · {sub.status === "active" ? "Active" : sub.status === "cancelling" ? "Ends soon" : "Payment issue"}
                <span className="pointer-events-none absolute right-0 sm:right-auto sm:left-0 top-full z-20 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-md bg-zinc-950 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 font-normal">
                  {sub.credits_remaining} of {sub.credits_total} listings left this month
                  {sub.credit_period_end ? (sub.cancel_at_period_end || sub.status === "cancelling"
                    ? ` · plan ends ${new Date(sub.paid_until || sub.credit_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, no further charges`
                    : ` · credits renew ${new Date(sub.credit_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`) : ""}.
                  Click for your plan &amp; billing settings.
                </span>
              </button>
            ) : (
              <Link to="/plans" data-testid="plan-chip"
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 text-zinc-500 px-2.5 py-1 rounded-full hover:border-zinc-400 hover:text-zinc-800 transition-colors duration-200">
                No plan · view plans
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="hidden sm:flex gap-1 mt-8 border-b border-zinc-200 overflow-x-auto" data-testid="employer-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`employer-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors duration-200 cursor-pointer ${tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "menu" && (
        <MobileMenuHome
          name={user?.company_name || "Your company"}
          subtitle={sub?.plan_name ? `${sub.plan_name} plan` : "Employer dashboard"}
          stat={sub ? { label: "Listings left this month", value: `${sub.credits_remaining} of ${sub.credits_total}`, hint: sub.plan_name } : null}
          items={TABS.map((t) => ({ id: t.id, label: typeof t.label === "string" ? t.label : "Messages", badge: t.id === "messages" ? msgUnread : 0 }))}
          onSelect={(id) => setTab(id)}
        />
      )}
      {tab !== "menu" && (
        <MobileBackHeader
          title={(() => { const l = TABS.find((t) => t.id === tab)?.label; return typeof l === "string" ? l : "Messages"; })()}
          onBack={() => setTab("menu")}
        />
      )}

      {tab === "settings" && (
      <div className="mt-6 sm:mt-10 space-y-10 pb-24" data-testid="settings-tab">
      {!isTeamMember && (
      <section data-testid="settings-messaging">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Messaging</p>
        <div className="glass rounded-lg p-6 sm:p-8">
          <div className="flex items-center justify-between gap-4">
            <div className="relative group flex items-start gap-2">
              <div>
                <h2 className="font-display text-lg font-bold text-zinc-950">Candidates can message first</h2>
                <p className="text-sm text-zinc-500 mt-1 max-w-xl">
                  When on, a candidate can start a chat with your team right after they apply, a quick hello, a question, anything. When off, only your team can start the conversation.
                </p>
              </div>
              <Info className="h-4 w-4 text-zinc-400 shrink-0 mt-1 cursor-help" data-testid="messaging-info" />
              <span data-testid="messaging-tooltip" className="pointer-events-none absolute left-0 top-full mt-2 w-72 rounded-md bg-zinc-900 px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg z-10">
                Every conversation is shared with your whole team in the Messages tab, anyone can pick it up and reply.
              </span>
            </div>
            <button onClick={toggleCandidateChat} data-testid="candidate-chat-toggle" aria-pressed={chatOpen}
              className={`shrink-0 h-9 px-5 text-xs font-semibold rounded-md border transition-colors duration-200 ${chatOpen ? "bg-blue-600 border-blue-600 text-white hover:bg-blue-700" : "bg-zinc-50 border-zinc-300 text-zinc-600 hover:border-zinc-400"}`}>
              {chatOpen ? "On" : "Off"}
            </button>
          </div>
        </div>
      </section>
      )}
      {!isTeamMember && (
      <section data-testid="settings-company">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Company</p>
      <div className="glass rounded-lg p-6 sm:p-8" data-testid="company-profile-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-zinc-950">Company profile</h2>
            <p className="text-sm text-zinc-500 mt-1">
              {user?.company_description
                ? user.company_description.slice(0, 120) + (user.company_description.length > 120 ? "…" : "")
                : "Tell candidates who you are. This fills your public company page."}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {user?.company_slug && !isTeamMember && (
              <Link to={`/companies/${user.company_slug}`} data-testid="view-company-page"
                className="h-9 px-4 inline-flex items-center border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                View public page
              </Link>
            )}
            {!isTeamMember && (
            <button onClick={openCompanyEditor} data-testid="edit-company-profile"
              className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
              Edit profile
            </button>
            )}
          </div>
        </div>
        <div className="border-t border-zinc-200 mt-6 pt-6" data-testid="verification-card">
        <div className="flex items-center gap-2 mb-1">
          <BadgeCheck className="h-4 w-4 text-blue-600" />
          <h2 className="font-display text-lg font-bold text-zinc-950">Company verification</h2>
        </div>
        {user?.verification_status === "verified" ? (
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-3 mt-3" data-testid="verification-verified">
            Verified. Your jobs can go live and candidates see a verified badge on your listings.
          </p>
        ) : user?.verification_status === "pending" ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 mt-3" data-testid="verification-pending">
            Being reviewed. We check these as quickly as we can and email you the outcome. Your jobs can go live once it's done.
          </p>
        ) : (
          <form onSubmit={submitVerification} className="mt-4 space-y-4" data-testid="verification-form">
            <p className="text-sm text-zinc-600 leading-relaxed">
              To keep the board free of scams, we verify every company before its jobs go live. These details are confidential,
              never shared or shown publicly. See the <a href="/terms" className="text-blue-700 font-medium">terms</a>.
              {user?.verification_status === "rejected" && (
                <span className="block mt-2 text-red-700 font-medium">Your last submission wasn't approved. Update the details and try again.</span>
              )}
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Companies House number *</label>
                <input required data-testid="v-company-number" value={verifyForm.company_number}
                  onChange={(e) => setVerifyForm({ ...verifyForm, company_number: e.target.value })}
                  placeholder="e.g. 12345678" readOnly={!!user?.ch_locked}
                  title={user?.ch_locked ? "Locked to your official Companies House record" : undefined}
                  className={`w-full h-11 px-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 ${user?.ch_locked ? "border-zinc-200 bg-zinc-50 text-zinc-500" : "border-zinc-300 bg-white"}`} />
                {user?.ch_locked && <p className="text-xs text-zinc-400 mt-1">From your official Companies House record, no need to type it.</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Director's full name *</label>
                <input required data-testid="v-director" value={verifyForm.director_name}
                  onChange={(e) => setVerifyForm({ ...verifyForm, director_name: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Registered address *</label>
              <input required data-testid="v-address" value={verifyForm.registered_address}
                onChange={(e) => setVerifyForm({ ...verifyForm, registered_address: e.target.value })}
                placeholder="As registered at Companies House" readOnly={!!user?.ch_locked}
                title={user?.ch_locked ? "Locked to your official Companies House record" : undefined}
                className={`w-full h-11 px-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 ${user?.ch_locked ? "border-zinc-200 bg-zinc-50 text-zinc-500" : "border-zinc-300 bg-white"}`} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex items-center justify-center gap-2 h-11 border border-dashed border-zinc-300 rounded-md text-xs font-semibold text-zinc-600 cursor-pointer hover:border-zinc-500 transition-colors duration-200" data-testid="v-doc-incorporation">
                <Upload className="h-3.5 w-3.5" /> Incorporation certificate
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                  onChange={(e) => uploadVerifyDoc(e, "verification")} disabled={verifyUploading} />
              </label>
              <label className="flex items-center justify-center gap-2 h-11 border border-dashed border-zinc-300 rounded-md text-xs font-semibold text-zinc-600 cursor-pointer hover:border-zinc-500 transition-colors duration-200" data-testid="v-doc-ownership">
                <Upload className="h-3.5 w-3.5" /> Proof of ownership / director ID
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                  onChange={(e) => uploadVerifyDoc(e, "verification")} disabled={verifyUploading} />
              </label>
            </div>
            {verifyDocs.length > 0 && (
              <ul className="space-y-1" data-testid="v-doc-list">
                {verifyDocs.map((d) => (
                  <li key={d.path} className="text-xs text-zinc-600 flex items-center justify-between bg-white border border-zinc-200 rounded-md px-3 py-2">
                    {d.filename}
                    <button type="button" onClick={() => setVerifyDocs((x) => x.filter((y) => y.path !== d.path))}
                      className="text-red-600 font-semibold" data-testid={`v-doc-remove-${d.filename}`}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
            <button type="submit" disabled={verifySaving || verifyUploading} data-testid="verification-submit"
              className="h-11 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
              {verifySaving ? "Submitting…" : "Submit for verification"}
            </button>
          </form>
        )}
        </div>
      </div>
      </section>
      )}

      <section data-testid="settings-team">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Team &amp; access</p>
      {(!isTeamMember || team.length > 0) && (
        <div className="glass rounded-lg p-6 sm:p-8" data-testid="team-card">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-blue-600" />
            <h2 className="font-display text-lg font-bold text-zinc-950">Team</h2>
          </div>
          {!isTeamMember && (
            <>
              <p className="text-sm text-zinc-500 mt-1">Invite colleagues to work on the same jobs and applicants. You choose what each person can do, and you can change it any time.</p>
              <form onSubmit={inviteMember} className="mt-4 flex flex-wrap gap-2">
                <input value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                  placeholder="Colleague's name" data-testid="team-invite-name" required
                  className="flex-1 min-w-[10rem] h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <input type="email" value={teamForm.email} onChange={(e) => setTeamForm({ ...teamForm, email: e.target.value })}
                  placeholder="colleague@company.co.uk" data-testid="team-invite-email" required
                  className="flex-1 min-w-[10rem] h-10 px-3 border border-zinc-300 rounded-md text-sm" />
                <select value={teamForm.role} onChange={(e) => setTeamForm({ ...teamForm, role: e.target.value })}
                  data-testid="team-invite-role" aria-label="Role"
                  className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                  <option value="recruiter">Recruiter</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button type="submit" disabled={teamBusy} data-testid="team-invite-submit"
                  className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                  {teamBusy ? "Sending…" : "Send invite"}
                </button>
              </form>
              <p className="text-xs text-zinc-400 mt-2">
                Recruiters manage jobs and applicants. Admins can also look after billing and the team. Viewers can see everything but change nothing.
              </p>
            </>
          )}
          {team.length > 0 && (
            <>
              <div className="flex flex-wrap gap-2 mt-5" data-testid="team-filters">
                {["all", "admin", "recruiter", "viewer"].map((r) => (
                  <button key={r} onClick={() => setTeamFilter({ ...teamFilter, role: r })}
                    data-testid={`team-filter-${r}`}
                    className={`h-8 px-3 text-xs font-semibold rounded-full border transition-colors duration-200 ${
                      teamFilter.role === r ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}>
                    {r === "all" ? "All roles" : `${r[0].toUpperCase()}${r.slice(1)}s`}
                  </button>
                ))}
                <span className="w-px bg-zinc-200 mx-1 hidden sm:block" />
                {["all", "active", "deactivated"].map((s) => (
                  <button key={s} onClick={() => setTeamFilter({ ...teamFilter, status: s })}
                    data-testid={`team-filter-status-${s}`}
                    className={`h-8 px-3 text-xs font-semibold rounded-full border transition-colors duration-200 ${
                      teamFilter.status === s ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}>
                    {s[0].toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <ul className="mt-3 space-y-2" data-testid="team-list">
              {filteredTeam.map((m) => (
                <li key={m.id} className="flex items-center justify-between border border-zinc-200 rounded-md px-4 py-2.5" data-testid={`team-member-${m.id}`}>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">
                      {m.name}
                      {m.is_owner && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white px-1.5 py-0.5 rounded">Owner</span>}
                      {!m.is_owner && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded"
                          data-testid={`team-role-badge-${m.id}`}>{m.team_role || "recruiter"}</span>
                      )}
                      {!m.is_active && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-600 px-1.5 py-0.5 rounded">Deactivated</span>}
                    </p>
                    <p className="text-xs text-zinc-500">{m.email}</p>
                    <p className="text-xs text-zinc-400 mt-0.5" data-testid={`team-member-meta-${m.id}`}>
                      Joined {m.created_at ? new Date(m.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "recently"}
                      {" · "}
                      {m.last_seen ? `last active ${new Date(m.last_seen).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : "not active yet"}
                    </p>
                  </div>
                  {!isTeamMember && !m.is_owner && (
                    <div className="flex items-center gap-2">
                      <select value={m.team_role || "recruiter"} onChange={(e) => setMemberRole(m.id, e.target.value)}
                        data-testid={`team-role-${m.id}`} aria-label={`Role for ${m.name}`}
                        className="h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white">
                        <option value="recruiter">Recruiter</option>
                        <option value="admin">Admin</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button onClick={() => removeMember(m.id)} data-testid={`team-remove-${m.id}`}
                        className="text-xs font-semibold text-red-600 hover:text-red-800">Remove</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            </>
          )}
        </div>
      )}
      </section>

      <section data-testid="settings-notifications-section">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Email notifications</p>
      <div data-testid="settings-notifications">
        <NotificationPrefs />
      </div>
      </section>

      <section data-testid="settings-security">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Account security</p>
      <AccountVerification />
      </section>

      {!isTeamMember && (
      <section data-testid="settings-billing">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Plan & billing</p>
      <div className="glass rounded-lg p-6 sm:p-8">
        {sub && sub.offer_pending ? (
          <div data-testid="offer-card">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Special plan offer</span>
            <h2 className="font-display text-lg font-bold text-zinc-950 mt-2.5" data-testid="offer-plan-name">{sub.plan_name}</h2>
            <p className="text-sm text-zinc-600 mt-1.5 max-w-lg" data-testid="offer-summary">
              HiredToday put this plan together for your company:
              <strong> £{Number(sub.monthly_price).toFixed(2)}/month</strong> for
              <strong> {sub.included_jobs} job credit{sub.included_jobs === 1 ? "" : "s"}</strong> each month
              {sub.extra_job_price > 0 && <>, extra listings £{Number(sub.extra_job_price).toFixed(2)} each</>}.
              Nothing is charged until you accept and check out securely with Stripe.
            </p>
            <p className="text-xs text-zinc-500 mt-2 max-w-lg">
              Credits renew monthly and unused ones don't roll over. You can cancel any time from this page, the plan then stays live until the end of the paid month.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={acceptOffer} disabled={subBusy} data-testid="offer-accept"
                title="Opens Stripe's secure checkout, your plan starts the moment payment completes"
                className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {subBusy ? "Opening checkout…" : `Accept & pay £${Number(sub.monthly_price).toFixed(2)}/mo`}
              </button>
              <a href="mailto:office@hiredtoday.co.uk?subject=Custom%20plan%20question" data-testid="offer-question"
                title="Not sure about something? Ask us before you decide"
                className="h-10 px-4 inline-flex items-center border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                Ask a question first
              </a>
            </div>
          </div>
        ) : sub && sub.status !== "cancelled" ? (
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-display text-lg font-bold text-zinc-950" data-testid="billing-plan-name">{sub.plan_name}</h2>
              <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                sub.status === "active" ? "bg-green-100 text-green-700" : sub.status === "cancelling" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}
                data-testid="billing-plan-status">
                {sub.status === "active" ? "Active" : sub.status === "cancelling" ? "Ends at renewal" : "Payment issue"}
              </span>
              {sub.annual && <span className="text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Annual billing</span>}
            </div>
            <div className="mt-4 max-w-md">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-zinc-600">Monthly listings left</span>
                <span className="font-bold text-zinc-950" data-testid="billing-credits">{sub.monthly_remaining} of {sub.credits_total}</span>
              </div>
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-600 rounded-full transition-all duration-500"
                  style={{ width: `${sub.credits_total ? (sub.monthly_remaining / sub.credits_total) * 100 : 0}%` }} />
              </div>
              {(sub.topup_credits > 0 || sub.urgent_credits > 0) && (
                <p className="text-xs font-medium text-zinc-700 mt-2" data-testid="billing-topup-balance">
                  {sub.topup_credits > 0 && `Plus ${sub.topup_credits} top-up listing credit${sub.topup_credits === 1 ? "" : "s"}, yours until used`}
                  {sub.topup_credits > 0 && sub.urgent_credits > 0 && " · "}
                  {sub.urgent_credits > 0 && `${sub.urgent_credits} urgent label${sub.urgent_credits === 1 ? "" : "s"} ready`}
                </p>
              )}
              <p className="text-xs text-zinc-500 mt-2" data-testid="billing-renewal">
                {(sub.cancel_at_period_end || sub.status === "cancelling") ? (
                  <>Your plan is cancelled, credits stay live until {new Date(sub.paid_until || sub.credit_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}, then the plan ends. Nothing more is charged.</>
                ) : (
                  <>Credits renew {sub.credit_period_end ? new Date(sub.credit_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long" }) : "monthly"}
                  {sub.paid_until && !sub.custom ? ` · next payment ${new Date(sub.paid_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : ""}.
                  Unused credits do not roll over. Changes to your plan are below.</>
                )}
              </p>
              {sub.pending_plan_key && (
                <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mt-2"
                  data-testid="billing-pending-downgrade">
                  Your plan changes to {sub.pending_plan_name} on{" "}
                  {sub.pending_plan_effective
                    ? new Date(sub.pending_plan_effective).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                    : "your next renewal"}, nothing to pay today. Visit the plans page to undo.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/plans" data-testid="billing-view-plans"
                className="h-9 px-4 inline-flex items-center border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                View plans
              </Link>
              {sub.status === "cancelling" ? (
                <button onClick={resumeSub} disabled={subBusy} data-testid="billing-resume"
                  className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                  {subBusy ? "Working…" : "Keep my plan"}
                </button>
              ) : (
                <button onClick={() => setCancelOpen(true)} disabled={subBusy} data-testid="billing-cancel"
                  className="h-9 px-4 border border-red-300 text-red-700 text-xs font-semibold rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-50">
                  Cancel plan
                </button>
              )}
            </div>
            <div className="mt-5 border-t border-zinc-100 pt-4 max-w-lg" data-testid="billing-topup">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Top up</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-zinc-700">
                <input type="number" min="0" max="200" value={topup.credits}
                  onChange={(e) => setTopup({ ...topup, credits: e.target.value })}
                  data-testid="topup-credits"
                  className="w-20 h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <span>extra listings (£{Number(sub.extra_job_price).toFixed(2)} each)</span>
                <input type="number" min="0" max="200" value={topup.urgent}
                  onChange={(e) => setTopup({ ...topup, urgent: e.target.value })}
                  data-testid="topup-urgent"
                  className="w-20 h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <span>urgent labels (£{Number(urgentPrice).toFixed(2)} each)</span>
                <button onClick={buyTopup}
                  disabled={topupBusy || ((Number(topup.credits) || 0) + (Number(topup.urgent) || 0)) < 1}
                  data-testid="topup-buy"
                  className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                  {topupBusy ? "Working…" : `Buy · £${((Number(topup.credits) || 0) * (sub.extra_job_price || 0) + (Number(topup.urgent) || 0) * urgentPrice).toFixed(2)}`}
                </button>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                Top-up listing credits stay until you use them, only your monthly allowance renews.
                Urgent labels never expire{sub.urgent_credits ? ` · you have ${sub.urgent_credits}` : ""}.
              </p>
            </div>
            {subInvoices.length > 0 && (
              <div className="mt-6" data-testid="billing-invoices">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Invoices</p>
                <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-md bg-white">
                  {subInvoices.map((inv) => (
                    <li key={inv.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`invoice-${inv.id}`}>
                      <span className="text-zinc-700">{inv.number || inv.id.slice(-8)}</span>
                      <span className="text-zinc-500 text-xs">{new Date(inv.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                      <span className="font-semibold text-zinc-900">£{inv.amount.toFixed(2)}</span>
                      {inv.url ? (
                        <a href={inv.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-700 hover:text-blue-900">View</a>
                      ) : <span className={`text-xs font-semibold ${inv.status === "paid" ? "text-green-700" : "text-zinc-500"}`}>{inv.status}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-zinc-950">No hiring plan</h2>
              <p className="text-sm text-zinc-500 mt-1">You pay per listing. Hiring regularly? A monthly plan brings the per-listing cost down.</p>
            </div>
            <Link to="/plans" data-testid="billing-view-plans"
              className="h-10 px-5 inline-flex items-center bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 shrink-0">
              View plans
            </Link>
          </div>
        )}
      </div>
      </section>
      )}

      {cancelOpen && sub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/50" data-testid="cancel-dialog"
          onClick={() => !subBusy && setCancelOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-xl font-bold text-zinc-950">We're sorry to see you go</h2>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              If you cancel, your {sub.plan_name} plan stays fully active until{" "}
              {sub.paid_until ? new Date(sub.paid_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "the end of your paid period"},
              and no further payments are taken after that. Your live listings carry on until they expire.
            </p>
            <label className="text-xs font-medium text-zinc-600 block mt-5 mb-1" htmlFor="cancel-reason">
              The main reason you're leaving (optional)
            </label>
            <select id="cancel-reason" value={cancelForm.reason} data-testid="cancel-reason"
              onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
              className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">Prefer not to say</option>
              <option value="hiring_pause">We've paused hiring</option>
              <option value="found_alternative">We found an alternative</option>
              <option value="too_expensive">It's more than we want to spend</option>
              <option value="not_enough_value">We didn't get enough value from it</option>
              <option value="other">Something else</option>
            </select>
            <textarea value={cancelForm.comment} rows={2} data-testid="cancel-comment"
              onChange={(e) => setCancelForm({ ...cancelForm, comment: e.target.value })}
              placeholder="Anything you'd like us to know? It helps, but it's entirely optional."
              className="w-full mt-3 px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            {retentionOffer && (
              <div className="mt-5 border border-blue-200 bg-blue-50/60 rounded-md p-4" data-testid="retention-offer">
                <p className="text-sm font-semibold text-zinc-900">
                  Before you go, {retentionOffer.percent}% off your next {retentionOffer.months} monthly payments
                </p>
                <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
                  A thank-you for hiring with us. Take it and your plan simply carries on, same credits, nothing else changes.
                </p>
                <button onClick={() => cancelSub(true)} disabled={subBusy} data-testid="retention-accept"
                  className="mt-3 w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                  {subBusy ? "Applying…" : `Accept ${retentionOffer.percent}% off and keep my plan`}
                </button>
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-2 mt-5">
              <button onClick={() => cancelSub(false)} disabled={subBusy} data-testid="cancel-confirm"
                className="flex-1 h-11 border border-red-300 text-red-700 text-sm font-semibold rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-50">
                {subBusy ? "Working…" : "Continue cancellation"}
              </button>
              <button onClick={() => setCancelOpen(false)} disabled={subBusy} data-testid="cancel-dismiss"
                className="h-11 px-5 border border-zinc-300 text-sm font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                Keep my plan
              </button>
            </div>
            <p className="text-xs text-zinc-400 mt-3">No phone calls, no forms, cancelling here is all it takes.</p>
          </div>
        </div>
      )}

      <div data-testid="settings-advanced">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3 mt-2">Advanced · careers page, widget, API and automation</p>
        <EmployerIntegrations />
      </div>
      </div>
      )}

      {tab === "overview" && (
      <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6 sm:mt-10" data-testid="employer-stats">
        {[
          { label: "Live jobs", value: perf ? perf.live_jobs : totals.live, desc: "Listings candidates can see and apply to right now" },
          { label: "Total views", value: perf ? perf.views : totals.views, desc: "Times your listings were opened in the last 30 days" },
          { label: "Apply clicks", value: perf ? perf.apply_starts : "–", desc: "Times a candidate started an application in the last 30 days" },
          { label: "Applications", value: perf ? perf.applications : totals.applications, desc: "Completed applications received in the last 30 days" },
          { label: "Conversion", value: perf ? `${perf.conversion}%` : "–", desc: "Applications divided by views over the last 30 days" },
        ].map((s) => (
          <div key={s.label} title={s.desc} className="border border-zinc-200 rounded-md p-5 bg-white" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="font-display text-2xl sm:text-3xl font-black text-zinc-950">{s.value}</p>
            <p className="text-xs sm:text-sm text-zinc-500 mt-1">{s.label}</p>
            <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-6" />
      </>
      )}

      {tab === "analytics" && (
        <div data-testid="analytics-tab">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">Live view of how your listings perform.</p>
            <button onClick={refreshAnalytics} data-testid="an-refresh"
              className="h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
              Refresh
            </button>
          </div>
          {!analytics ? (
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 rounded-md bg-zinc-50 animate-pulse" />)}
            </div>
          ) : <AnalyticsPanel a={analytics} />}
        </div>
      )}

      {tab === "listings" && (
      <div className="mt-6 sm:mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="hidden sm:block font-display text-xl font-bold text-zinc-950">Your listings</h2>
          <label className="flex items-center gap-2 text-xs font-medium text-zinc-500 cursor-pointer" data-testid="show-archived-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => { setShowArchived(e.target.checked); setListingPage(1); }} className="h-4 w-4 accent-zinc-900" />
            Show archived
          </label>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 mb-4" data-testid="listings-filters">
          <input value={listingSearch} onChange={(e) => { setListingSearch(e.target.value); setListingPage(1); }}
            placeholder="Search title or location" data-testid="listings-search"
            className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <select value={listingStatus} onChange={(e) => { setListingStatus(e.target.value); setListingPage(1); }} data-testid="listings-status"
            className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
            <option value="">All statuses</option>
            {Object.entries(JOB_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={listingSort} onChange={(e) => { setListingSort(e.target.value); setListingPage(1); }} data-testid="listings-sort"
            className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="views">Most views</option>
            <option value="applicants">Most applicants</option>
            <option value="az">Title A–Z</option>
          </select>
        </div>
        {loading ? (
          <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
        ) : jobs.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="no-jobs-yet">
            <p className="font-display text-xl font-bold text-zinc-900">No jobs posted yet.</p>
            <p className="mt-2 text-sm text-zinc-500">Your first one can be live today. Start free.</p>
          </div>
        ) : (
          <div className="border border-zinc-200 rounded-md" data-testid="employer-jobs-list">
            {filteredJobs.length === 0 && (
              <p className="p-6 text-sm text-zinc-500" data-testid="listings-no-match">No listings match your search or filters.</p>
            )}
            {pageSlice(filteredJobs, listingPage).map((job, i) => {
              const st = JOB_STATUS[job.status] || JOB_STATUS.pending_review;
              const salary = formatSalary(job);
              return (
                <div key={job.id} data-testid={`employer-job-${job.slug}`}
                  className={`p-5 bg-white flex flex-col lg:flex-row lg:items-center gap-4 justify-between first:rounded-t-md last:rounded-b-md ${i > 0 ? "border-t border-zinc-100" : ""}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Link to={`/jobs/${job.slug}`} className="font-display font-bold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
                        {job.title}
                      </Link>
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${st.cls}`} data-testid={`job-status-${job.slug}`}>{st.label}</span>
                      {job.tier === "featured" && (
                        <span className="text-[11px] font-bold uppercase tracking-wider bg-yellow-400 text-zinc-950 px-2 py-0.5 rounded">Featured</span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500 mt-1">
                      {job.location}{salary ? ` · ${salary}` : ""} ·{" "}
                      <HoverTip label={`${job.views || 0} views`} testid={`views-tip-${job.slug}`} onEnter={() => loadInsights(job.id)}>
                        <ViewsTip data={insights[job.id]} />
                      </HoverTip>
                      {" · "}
                      <HoverTip label={`${job.applications_count || 0} applicants`} testid={`applicants-tip-${job.slug}`} onEnter={() => loadInsights(job.id)}>
                        <ApplicantsTip data={insights[job.id]} />
                      </HoverTip>
                      {job.apply_method === "external" ? ` · ${job.external_clicks || 0} clicks to your site` : ""}
                      {job.status === "live" && job.expires_at ? ` · live until ${new Date(job.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ` · posted ${new Date(job.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                    </p>
                    {job.status === "rejected" && job.admin_note && (
                      <p className="text-sm text-red-600 mt-1">Note from our team: {job.admin_note}</p>
                    )}
                    {job.status === "live" && job.seo && (
                      <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5" data-testid={`seo-health-${job.slug}`}>
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${job.seo.status === 200 ? "bg-green-500" : "bg-amber-400"}`} />
                        Google ·{" "}
                        <HoverTip label={`${job.seo.locations} location${job.seo.locations === 1 ? "" : "s"} posted`}
                          testid={`locations-tip-${job.slug}`} onEnter={() => loadInsights(job.id)}>
                          <LocationsTip data={insights[job.id]} />
                        </HoverTip>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    {job.status === "awaiting_payment" && (
                      <button onClick={() => payNow(job)} data-testid={`pay-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 bg-amber-500 text-white text-xs font-semibold rounded-md hover:bg-amber-600 transition-colors duration-200">
                        <CreditCard className="h-3.5 w-3.5" /> Complete payment
                      </button>
                    )}
                    <button onClick={() => openApplicants(job)} data-testid={`applicants-${job.slug}`}
                      className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                      <Users className="h-3.5 w-3.5" /> Applicants
                    </button>
                    <button onClick={() => setReportJob(job)} data-testid={`report-${job.slug}`}
                      className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                      <BarChart3 className="h-3.5 w-3.5" /> Report
                    </button>
                    <button onClick={() => startEdit(job)} data-testid={`edit-${job.slug}`}
                      className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    {(job.tier !== "featured" || !job.urgent) && ["live", "pending_review"].includes(job.status) && (
                      <button onClick={() => { setUpgradeJob(job); setUpgradePromo(null); setUpgradePromoCode(""); setUpgradePromoError(""); }} data-testid={`promote-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 border border-yellow-400 bg-yellow-50 text-yellow-800 text-xs font-semibold rounded-md hover:bg-yellow-100 transition-colors duration-200">
                        <Zap className="h-3.5 w-3.5" /> Promote
                      </button>
                    )}
                    {job.status === "live" && (
                      <button onClick={() => togglePause(job)} data-testid={`pause-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                        <Pause className="h-3.5 w-3.5" /> Pause
                      </button>
                    )}
                    {job.status === "paused" && (
                      <button onClick={() => togglePause(job)} data-testid={`resume-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 border border-green-300 bg-green-50 text-green-700 text-xs font-semibold rounded-md hover:bg-green-100 transition-colors duration-200">
                        <Play className="h-3.5 w-3.5" /> Resume
                      </button>
                    )}
                    {job.status === "live" && (
                      <button onClick={() => toggleClose(job)} data-testid={`close-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                        <XCircle className="h-3.5 w-3.5" /> Close
                      </button>
                    )}
                    {["closed", "expired", "archived"].includes(job.status) && (
                      <button onClick={() => toggleClose(job)} data-testid={`reopen-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                        <RotateCcw className="h-3.5 w-3.5" /> Reopen
                      </button>
                    )}
                    {["closed", "expired", "rejected", "archived", "paused"].includes(job.status) && (
                      <button onClick={() => repostJob(job)} data-testid={`repost-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                        <FileUp className="h-3.5 w-3.5" /> Repost
                      </button>
                    )}
                    {["live", "closed", "expired", "rejected", "awaiting_payment", "paused"].includes(job.status) && (
                      <button onClick={() => archiveJob(job)} data-testid={`archive-${job.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                        <Archive className="h-3.5 w-3.5" /> Archive
                      </button>
                    )}
                    <button onClick={() => removeJob(job)} data-testid={`delete-${job.slug}`}
                      className="inline-flex items-center gap-1.5 h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors duration-200">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pager page={listingPage} total={filteredJobs.length} onChange={setListingPage} testid="listings-pager" />
      </div>
      )}

      {/* Applicants dialog */}
      <Dialog open={!!applicantsFor} onOpenChange={() => setApplicantsFor(null)}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="applicants-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">
              Applicants: {applicantsFor?.title}
            </DialogTitle>
          </DialogHeader>
          {loadingApps ? (
            <div className="py-8 space-y-3" data-testid="applicants-loading">
              {[...Array(2)].map((_, i) => <div key={`apps-skel-${i}`} className="h-20 bg-zinc-100 rounded-md animate-pulse" />)}
            </div>
          ) : applicants.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6" data-testid="no-applicants">No applications yet. Share the listing to get the word out.</p>
          ) : (
            <div className="space-y-4 mt-2">
              {pageSlice(applicants, appPage).map((a) => (
                <div key={a.id} className="border border-zinc-200 rounded-md p-5" data-testid={`applicant-${a.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-semibold text-zinc-950">{a.candidate_name}</p>
                      <p className="text-sm text-zinc-500">{a.candidate_email}{a.phone ? ` · ${a.phone}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={a.status} onChange={(e) => setAppStatus(a.id, e.target.value)}
                        data-testid={`applicant-status-${a.id}`}
                        className="h-9 px-2 border border-zinc-300 rounded-md text-xs bg-white">
                        {APP_STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                      <button onClick={() => setPanelAppId(a.id)} data-testid={`applicant-manage-${a.id}`}
                        className="inline-flex items-center gap-1 h-9 px-3 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                        <UserRound className="h-3.5 w-3.5" /> Manage
                      </button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button onClick={() => toggleProfile(a.id)} data-testid={`applicant-profile-${a.id}`}
                      className="text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors duration-200">
                      {loadingProfile === a.id ? "Loading…" : profiles[a.id] ? "Hide profile" : "View profile"}
                    </button>
                    {profiles[a.id] && (
                      <div className="mt-3 border border-zinc-200 rounded-md p-4 bg-zinc-50/60 space-y-3" data-testid={`applicant-profile-panel-${a.id}`}>
                        {profiles[a.id].headline && <p className="text-sm font-semibold text-zinc-900">{profiles[a.id].headline}</p>}
                        {profiles[a.id].about && <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">{profiles[a.id].about}</p>}
                        {profiles[a.id].looking_for && (
                          <p className="text-xs text-zinc-500"><span className="font-semibold text-zinc-700">Looking for:</span> {profiles[a.id].looking_for}</p>
                        )}
                        {profiles[a.id].skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {profiles[a.id].skills.map((s) => (
                              <span key={s} className="text-xs bg-white border border-zinc-200 text-zinc-700 px-2.5 py-1 rounded-full">{s}</span>
                            ))}
                          </div>
                        )}
                        {profiles[a.id].experience?.length > 0 && (
                          <div className="space-y-1.5">
                            {profiles[a.id].experience.map((x, i) => (
                              <p key={`${i}-${x.title || ""}`} className="text-xs text-zinc-600">
                                <span className="font-semibold text-zinc-800">{x.title}</span>
                                {x.company ? ` · ${x.company}` : ""}{x.period ? ` · ${x.period}` : ""}
                              </p>
                            ))}
                          </div>
                        )}
                        {!profiles[a.id].headline && !profiles[a.id].about && !profiles[a.id].experience?.length && !profiles[a.id].skills?.length && (
                          <p className="text-xs text-zinc-500">This candidate hasn't filled in their profile yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                  {a.cover_note && <p className="mt-3 text-sm text-zinc-700 leading-relaxed whitespace-pre-line border-l-2 border-zinc-200 pl-3">{a.cover_note}</p>}
                  {a.cv_link && (
                    <a href={a.cv_link.startsWith("http") ? a.cv_link : `https://${a.cv_link}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`applicant-cv-${a.id}`}
                      className="mt-3 inline-block text-sm font-medium text-blue-700 hover:text-blue-900">
                      View CV / profile
                    </a>
                  )}
                  {a.cv_path && /\.(jpe?g|png|webp)$/i.test(a.cv_filename || a.cv_path) && (
                    <button type="button" onClick={() => setCvViewer({ src: fileUrl(a.cv_path), alt: a.cv_filename || "CV photo" })}
                      data-testid={`applicant-cv-file-${a.id}`}
                      className="mt-3 ml-4 inline-block text-sm font-medium text-blue-700 hover:text-blue-900">
                      View CV{a.cv_filename ? ` (${a.cv_filename})` : ""}
                    </button>
                  )}
                  {a.cv_path && !/\.(jpe?g|png|webp)$/i.test(a.cv_filename || a.cv_path) && (
                    <a href={fileUrl(a.cv_path)} target="_blank" rel="noopener noreferrer"
                      data-testid={`applicant-cv-file-${a.id}`}
                      className="mt-3 ml-4 inline-block text-sm font-medium text-blue-700 hover:text-blue-900">
                      Download CV{a.cv_filename ? ` (${a.cv_filename})` : ""}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
          <Pager page={appPage} total={applicants.length} onChange={setAppPage} testid="applicants-pager" />
        </DialogContent>
      </Dialog>

      {cvViewer && <ImageLightbox src={cvViewer.src} alt={cvViewer.alt} testid="applicant-cv-viewer" onClose={() => setCvViewer(null)} />}
      {photoViewer && <ImageLightbox src={photoViewer.src} alt={photoViewer.alt} testid="company-photo-viewer" onClose={() => setPhotoViewer(null)} />}

      {tab === "messages" && (
        <div className="mt-6" data-testid="employer-messages-tab">
          <MessagesInbox role="employer" testid="emp-messages" />
        </div>
      )}
      {tab === "candidates" && (
        <div className="mt-6 sm:mt-10" data-testid="candidates-tab">
          <div className="flex flex-col md:flex-row gap-2 md:items-center mb-4">
            <input value={candSearch} onChange={(e) => { setCandSearch(e.target.value); setCandPage(1); }}
              placeholder="Search candidates by name or email" data-testid="candidates-search"
              className="flex-1 h-11 px-4 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <select value={candJob} onChange={(e) => { setCandJob(e.target.value); setCandPage(1); }} data-testid="candidates-job-filter"
              className="h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
              <option value="">All jobs</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <select value={candStatus} onChange={(e) => { setCandStatus(e.target.value); setCandPage(1); }} data-testid="candidates-status-filter"
              className="h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
              <option value="">Any status</option>
              {APP_STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          {candLoading ? (
            <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
          ) : candidates.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="candidates-empty">
              <p className="font-display text-xl font-bold text-zinc-900">No candidates found.</p>
              <p className="mt-2 text-sm text-zinc-500">Applications appear here as soon as candidates apply to your jobs.</p>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="candidates-list">
              {pageSlice(candidates, candPage).map((c, i) => (
                <div key={c.id} data-testid={`candidate-row-${c.id}`}
                  className={`p-4 bg-white flex items-center justify-between gap-3 flex-wrap ${i > 0 ? "border-t border-zinc-100" : ""}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1.5">
                      {c.name || "Candidate"}
                      {c.candidate_verified && <BadgeCheck className="h-4 w-4 text-blue-600" data-testid={`candidate-verified-${c.id}`} />}
                    </p>
                    <p className="text-xs text-zinc-500">{c.job_title}{c.email ? ` · ${c.email}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700" data-testid={`candidate-status-${c.id}`}>{c.status}</span>
                    <button onClick={() => setPanelAppId(c.id)} data-testid={`candidate-manage-${c.id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                      <UserRound className="h-3.5 w-3.5" /> Manage
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pager page={candPage} total={candidates.length} onChange={setCandPage} testid="candidates-pager" />
        </div>
      )}

      {tab === "interviews" && (
        <EmployerInterviews />
      )}
      {tab === "talent" && (
        <div className="mt-6 sm:mt-10" data-testid="talent-tab">
          <div className="flex flex-col md:flex-row gap-2 md:items-center mb-4">
            <input value={talentSearch} onChange={(e) => { setTalentSearch(e.target.value); setMatchMode(false); setTalentPage(1); }}
              placeholder="Search by name, headline or skill" data-testid="talent-search"
              className="flex-1 h-11 px-4 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <input value={talentLoc} onChange={(e) => { setTalentLoc(e.target.value); setMatchMode(false); setTalentPage(1); }}
              placeholder="Location (e.g. Leeds)" data-testid="talent-location"
              className="md:w-56 h-11 px-4 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button onClick={runTalentMatch} disabled={talentLoading} data-testid="talent-match-run"
              className="inline-flex items-center gap-1.5 h-11 px-4 bg-violet-600 text-white text-xs font-semibold rounded-md hover:bg-violet-700 disabled:opacity-50 transition-colors duration-200 whitespace-nowrap">
              <Sparkles className="h-3.5 w-3.5" /> Match to my roles
            </button>
          </div>
          {matchMode && (
            <p className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 mb-4" data-testid="talent-match-note">
              Ranked by fit to your current and past listings, every score shows its reasons on the card. Advisory only: nothing here advances or rejects anyone automatically.
            </p>
          )}
          {talentLoading ? (
            <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" />
          ) : talentError ? (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="talent-verification-note">
              <ShieldAlert className="h-6 w-6 text-zinc-400 mx-auto mb-3" />
              <p className="font-display text-xl font-bold text-zinc-900">Talent pool unlocks with company verification.</p>
              <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">{talentError}</p>
              <button onClick={() => setTab("settings")} data-testid="talent-verify-cta"
                className="mt-5 h-10 px-6 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Verify in Settings
              </button>
            </div>
          ) : talent.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="talent-empty">
              <p className="font-display text-xl font-bold text-zinc-900">No one matches that yet.</p>
              <p className="mt-2 text-sm text-zinc-500">Candidates who opt in to being contacted by verified employers appear here.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="talent-list">
              {pageSlice(talent, talentPage).map((p) => (
                <div key={p.id} className="border border-zinc-200 rounded-md p-5 bg-white flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`talent-card-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-display font-bold text-zinc-950">{p.name}
                      {p.verified && (
                        <BadgeCheck className="ml-1.5 inline h-4 w-4 text-blue-600 -mt-0.5" data-testid={`talent-verified-${p.id}`} />
                      )}
                      {p.match_score > 0 && (
                        <span className="ml-2 text-[11px] font-bold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full" data-testid={`talent-score-${p.id}`}>Fit score {p.match_score}</span>
                      )}
                    </p>
                    <p className="text-sm text-zinc-500 mt-0.5">{[p.headline, p.location].filter(Boolean).join(" · ") || "Profile details not added yet"}</p>
                    {(p.skills || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {p.skills.slice(0, 5).map((s) => <span key={s} className="bg-zinc-100 text-zinc-700 text-xs font-medium px-2 py-0.5 rounded-full">{s}</span>)}
                      </div>
                    )}
                    {(p.match_reasons || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`talent-reasons-${p.id}`}>
                        {p.match_reasons.map((r) => <span key={r} className="bg-violet-50 text-violet-700 border border-violet-200 text-xs font-medium px-2 py-0.5 rounded-full">{r}</span>)}
                      </div>
                    )}
                    {[p.availability, p.salary_expectation].filter(Boolean).length > 0 && (
                      <p className="text-xs text-zinc-400 mt-2">{[p.availability && `Available: ${p.availability}`, p.salary_expectation && `Salary: ${p.salary_expectation}`].filter(Boolean).join(" · ")}</p>
                    )}
                    {(p.latest_role || p.experience_years > 0) && (
                      <p className="text-xs text-zinc-500 mt-1.5" data-testid={`talent-exp-${p.id}`}>
                        Experience: {p.latest_role ? `${p.latest_role}${p.latest_company ? ` at ${p.latest_company}` : ""}` : ""}{p.latest_role && p.experience_years ? " · " : ""}{p.experience_years ? `${p.experience_years} role${p.experience_years === 1 ? "" : "s"} on profile` : ""}
                      </p>
                    )}
                    {p.looking_for && (
                      <p className="text-xs text-zinc-500 mt-1" data-testid={`talent-looking-${p.id}`}>Looking for: {p.looking_for}</p>
                    )}
                  </div>
                  <div className="flex flex-col sm:items-end gap-2 self-start sm:self-center">
                    <button onClick={() => { setInviteFor(p); setInviteJob(""); }} data-testid={`talent-invite-${p.id}`}
                      className="inline-flex items-center gap-1.5 h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 whitespace-nowrap">
                      Invite to apply
                    </button>
                    {(() => {
                      const req = talentRequests.find((r) => r.candidate_id === p.id);
                      if (req?.status === "approved" && req.contact) {
                        return (
                          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-1.5" data-testid={`talent-contact-${p.id}`}>
                            Contact shared: <a href={`mailto:${req.contact.email}`} className="font-semibold underline">{req.contact.email}</a>
                          </div>
                        );
                      }
                      if (req?.status === "pending") {
                        return <span className="text-xs text-zinc-500 font-medium" data-testid={`talent-requested-${p.id}`}>Contact request sent</span>;
                      }
                      if (req?.status === "declined") {
                        return <span className="text-xs text-zinc-400" data-testid={`talent-declined-${p.id}`}>Contact request declined</span>;
                      }
                      return (
                        <button onClick={() => requestAccess(p)} data-testid={`talent-request-${p.id}`}
                          className="inline-flex items-center h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200 whitespace-nowrap">
                          Request contact details
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pager page={talentPage} total={talent.length} onChange={setTalentPage} testid="talent-pager" />
          <p className="text-xs text-zinc-400 mt-4">Everyone here chose to be discoverable. Contact details are only shared when the candidate approves your request.</p>
        </div>
      )}

      {/* Talent invite dialog */}
      <Dialog open={!!inviteFor} onOpenChange={() => setInviteFor(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="talent-invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Invite {inviteFor?.name} to apply</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-zinc-600">Pick a live listing. We'll email {inviteFor?.name} a personal invite and flag it in their dashboard.</p>
            <select value={inviteJob} onChange={(e) => setInviteJob(e.target.value)} data-testid="talent-invite-job"
              className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
              <option value="">Choose a live job…</option>
              {liveJobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <button onClick={sendTalentInvite} disabled={!inviteJob} data-testid="talent-invite-send"
              className="w-full h-11 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
              Send invite
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recruitment analytics & candidate panel */}
      <JobReport jobId={reportJob?.id} onClose={() => setReportJob(null)} />
      <CandidatePanel applicationId={panelAppId} onClose={() => setPanelAppId(null)}
        onChanged={() => applicantsFor && openApplicants(applicantsFor)} />

      {/* Promote dialog */}
      <Dialog open={!!upgradeJob} onOpenChange={() => setUpgradeJob(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="promote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Promote "{upgradeJob?.title}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {upgradeJob?.tier === "free" && (
              <button onClick={() => doUpgrade(upgradeJob, "standard", false)} data-testid="promote-standard"
                className="w-full text-left border border-zinc-200 rounded-md p-4 hover:border-zinc-400 transition-colors duration-200">
                <p className="text-sm font-semibold text-zinc-950">Standard · £{pricing?.standard?.price ?? 20}</p>
                <p className="text-xs text-zinc-500 mt-0.5">Listed above free jobs.</p>
              </button>
            )}
            {upgradeJob?.tier !== "featured" && (
              <button onClick={() => doUpgrade(upgradeJob, "featured", false)} data-testid="promote-featured"
                className="w-full text-left border-2 border-yellow-300 bg-yellow-50/50 rounded-md p-4 hover:border-yellow-400 transition-colors duration-200">
                <p className="text-sm font-semibold text-zinc-950">Featured · £{pricing?.featured?.price ?? 49}</p>
                <p className="text-xs text-zinc-500 mt-0.5">Top of search, homepage placement, featured badge.</p>
              </button>
            )}
            {upgradeJob && !upgradeJob.urgent && (
              <button onClick={() => doUpgrade(upgradeJob, null, true)} data-testid="promote-urgent"
                className="w-full text-left border border-red-200 bg-red-50/40 rounded-md p-4 hover:border-red-300 transition-colors duration-200">
                <p className="text-sm font-semibold text-zinc-950">Urgent label · £{pricing?.urgent?.price ?? 9}</p>
                <p className="text-xs text-zinc-500 mt-0.5">Red urgent badge and boosted placement.</p>
              </button>
            )}
            <div className="pt-2 border-t border-zinc-100">
              <label className="text-xs font-medium text-zinc-600 block mb-1.5">Promo code (optional)</label>
              <div className="flex gap-2">
                <input data-testid="upgrade-promo-input" value={upgradePromoCode}
                  onChange={(e) => { setUpgradePromoCode(e.target.value.toUpperCase()); setUpgradePromo(null); setUpgradePromoError(""); }}
                  placeholder="e.g. LAUNCH25"
                  className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button type="button" onClick={applyUpgradePromo} data-testid="upgrade-promo-apply"
                  className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-zinc-700 hover:border-zinc-500 transition-colors duration-200">
                  Apply
                </button>
              </div>
              {upgradePromo && <p className="text-xs font-medium text-green-700 mt-1.5" data-testid="upgrade-promo-applied">{upgradePromo.code}: {upgradePromo.percent}% off applies to whichever option you pick.</p>}
              {upgradePromoError && <p className="text-xs font-medium text-red-600 mt-1.5" data-testid="upgrade-promo-error">{upgradePromoError}</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Company profile dialog */}
      <Dialog open={companyOpen} onOpenChange={setCompanyOpen}>
        <DialogContent className="bg-white sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="company-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Company profile</DialogTitle>
          </DialogHeader>
          {companyForm && (
            <form onSubmit={saveCompany} className="space-y-4 mt-2">
              {user?.ch_locked ? (
                <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50/60" data-testid="ch-locked-fields">
                  <div className="flex items-center gap-1.5 mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Official Companies House record</p>
                    <span className="group relative inline-flex" data-testid="ch-lock-info">
                      <button type="button" aria-label="Why are these details locked?" aria-describedby="ch-lock-tip"
                        className="text-zinc-400 hover:text-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded-full">
                        <Info className="h-3.5 w-3.5" />
                      </button>
                      <span id="ch-lock-tip" role="tooltip"
                        className="pointer-events-none absolute right-0 sm:right-auto sm:left-0 top-full z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-md bg-zinc-950 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
                        These details come from your official Companies House record and are protected so your public listings stay accurate. To change them, email office@hiredtoday.co.uk, suitable proof of authority and supporting company documents may be required.
                      </span>
                    </span>
                  </div>
                  <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <dt className="font-medium text-zinc-500">Legal name</dt><dd className="text-zinc-900 font-semibold" data-testid="ch-locked-name">{user.company_name}</dd>
                    <dt className="font-medium text-zinc-500">Company No</dt><dd className="text-zinc-900" data-testid="ch-locked-number">{user.company_number}</dd>
                    <dt className="font-medium text-zinc-500">Registered office</dt><dd className="text-zinc-900">{user.company_address || "—"}</dd>
                    <dt className="font-medium text-zinc-500">Status</dt><dd className="text-zinc-900 capitalize">{user.ch_status || "—"}</dd>
                  </dl>
                  <a href="mailto:office@hiredtoday.co.uk?subject=Companies%20House%20detail%20change%20request&body=Company%20number%3A%20"
                    data-testid="ch-request-change"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-blue-700 hover:text-blue-900">
                    Request a change <ArrowUpRight className="h-3 w-3" />
                  </a>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company name</label>
                  <input required data-testid="company-form-name" value={companyForm.company_name}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Trading name <span className="text-zinc-400 font-normal">(optional, shown instead if different)</span></label>
                <input data-testid="company-form-trading-name" value={companyForm.trading_name}
                  onChange={(e) => setCompanyForm({ ...companyForm, trading_name: e.target.value })}
                  placeholder="e.g. the name candidates know you by"
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Industry</label>
                  <input data-testid="company-form-industry" value={companyForm.company_industry}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_industry: e.target.value })}
                    placeholder="e.g. Logistics & transport"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company size</label>
                  <select data-testid="company-form-size" value={companyForm.company_size}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_size: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    <option value="">Choose…</option>
                    {["1–10", "11–50", "51–200", "201–500", "500+"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Website</label>
                <input data-testid="company-form-website" value={companyForm.company_website}
                  onChange={(e) => setCompanyForm({ ...companyForm, company_website: e.target.value })}
                  placeholder="https://"
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Brand colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" data-testid="company-form-brand-color" value={companyForm.brand_color || "#18181b"}
                      onChange={(e) => setCompanyForm({ ...companyForm, brand_color: e.target.value })}
                      className="h-11 w-14 border border-zinc-300 rounded-md cursor-pointer bg-white" />
                    <input data-testid="company-form-brand-color-hex" value={companyForm.brand_color}
                      onChange={(e) => setCompanyForm({ ...companyForm, brand_color: e.target.value })}
                      placeholder="#18181b"
                      className="flex-1 h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">Used on your careers page and website widget.</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Tagline</label>
                  <input data-testid="company-form-brand-tagline" value={companyForm.brand_tagline}
                    onChange={(e) => setCompanyForm({ ...companyForm, brand_tagline: e.target.value })}
                    placeholder="e.g. Moving Britain, one delivery at a time" maxLength={120}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Phone</label>
                  <input data-testid="company-form-phone" value={companyForm.phone}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    placeholder="e.g. 0113 000 0000"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                {!user?.ch_locked && (
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Companies House number</label>
                  <input data-testid="company-form-reg" value={companyForm.company_reg_number}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_reg_number: e.target.value })}
                    placeholder="e.g. 12345678"
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                )}
              </div>
              {!user?.ch_locked && (
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Business address</label>
                <input data-testid="company-form-address" value={companyForm.company_address}
                  onChange={(e) => setCompanyForm({ ...companyForm, company_address: e.target.value })}
                  placeholder="Street, town, postcode"
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              )}
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company photos</label>
                <div className="flex flex-wrap gap-3">
                  {(companyForm.company_photos || []).map((p) => (
                    <div key={p} className="relative" data-testid={`company-photo-${p.split("/").pop()}`}>
                      <button type="button" onClick={() => setPhotoViewer({ src: fileUrl(p), alt: "Company photo" })} className="block" data-testid="company-photo-view">
                        <img src={fileUrl(p)} alt="Company" className="h-20 w-20 rounded-md object-cover border border-zinc-200 hover:opacity-90 transition-opacity duration-200" />
                      </button>
                      <button type="button" onClick={() => setCompanyForm((f) => ({ ...f, company_photos: f.company_photos.filter((x) => x !== p) }))}
                        data-testid={`company-photo-remove-${p.split("/").pop()}`} aria-label="Remove photo"
                        className="absolute -top-2 -right-2 h-5 w-5 bg-zinc-950 text-white rounded-full text-[10px] font-bold leading-none">×</button>
                    </div>
                  ))}
                  {(companyForm.company_photos || []).length < 6 && (
                    <label className="h-20 w-20 inline-flex flex-col items-center justify-center gap-1 border border-dashed border-zinc-300 rounded-md text-[10px] font-semibold text-zinc-500 cursor-pointer hover:border-zinc-500 transition-colors duration-200" data-testid="company-photo-upload">
                      <Upload className="h-4 w-4" /> {photoUploading ? "…" : "Add photo"}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} disabled={photoUploading} />
                    </label>
                  )}
                </div>
                <p className="text-xs text-zinc-400 mt-1.5">Up to 6 photos of your workplace, team or vehicles. Shown on your public page.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Company logo</label>
                <div className="flex items-center gap-3">
                  {companyForm.company_logo && (
                    <img src={fileUrl(companyForm.company_logo)} alt="Logo preview" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="company-logo-preview"
                      className="h-11 w-11 rounded-md object-cover border border-zinc-200" />
                  )}
                  <label className="inline-flex items-center gap-2 h-11 px-4 border border-dashed border-zinc-300 rounded-md text-xs font-semibold text-zinc-600 cursor-pointer hover:border-zinc-500 transition-colors duration-200" data-testid="company-logo-upload">
                    <Upload className="h-3.5 w-3.5" /> {logoUploading ? "Uploading…" : "Upload logo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadLogo} disabled={logoUploading} />
                  </label>
                </div>
                <p className="text-xs text-zinc-400 mt-1.5">Shown on your public company page and next to your jobs.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">About the company</label>
                <textarea rows={5} data-testid="company-form-description" value={companyForm.company_description}
                  onChange={(e) => setCompanyForm({ ...companyForm, company_description: e.target.value })}
                  placeholder="What you do, where you are, what it's like to work there."
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y" />
              </div>
              <button type="submit" disabled={companySaving} data-testid="company-form-save"
                className="h-11 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {companySaving ? "Saving…" : "Save profile"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editJob} onOpenChange={() => setEditJob(null)}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Edit listing</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Job title</label>
                <input required data-testid="edit-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Location</label>
                  <input required data-testid="edit-location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Job type</label>
                  <select data-testid="edit-job-type" value={editForm.job_type} onChange={(e) => setEditForm({ ...editForm, job_type: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white">
                    {["Full-time", "Part-time", "Contract", "Temporary", "Apprenticeship"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Salary from (£)</label>
                  <input type="number" min="0" data-testid="edit-salary-min" value={editForm.salary_min} onChange={(e) => setEditForm({ ...editForm, salary_min: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 block mb-1.5">Salary to (£)</label>
                  <input type="number" min="0" data-testid="edit-salary-max" value={editForm.salary_max} onChange={(e) => setEditForm({ ...editForm, salary_max: e.target.value })}
                    className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Description</label>
                <textarea required rows={8} data-testid="edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y" />
              </div>
              <div className="border border-zinc-200 rounded-md p-4" data-testid="edit-location-boost">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-zinc-950">Location Boost</p>
                    <p className="text-xs text-zinc-500 mt-0.5">This job also appears in searches for these nearby areas (up to 10). Suggestions follow the location above.</p>
                  </div>
                  <button type="button" onClick={suggestEditBoost} disabled={editBoostLoading || !(editForm.location || "").trim()} data-testid="edit-boost-suggest"
                    className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 whitespace-nowrap transition-colors duration-200">
                    {editBoostLoading ? "Finding…" : "Suggest nearby areas"}
                  </button>
                </div>
                {(editForm.boost_locations || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="edit-boost-chips">
                    {editForm.boost_locations.map((l) => (
                      <span key={l} className="inline-flex items-center gap-1.5 bg-zinc-100 text-zinc-800 text-xs font-medium px-2.5 py-1.5 rounded-full"
                        data-testid={`edit-boost-chip-${l.replace(/\s+/g, "-").toLowerCase()}`}>
                        {l}
                        <button type="button" onClick={() => setEditForm({ ...editForm, boost_locations: editForm.boost_locations.filter((x) => x !== l) })}
                          aria-label={`Remove ${l}`} data-testid={`edit-boost-remove-${l.replace(/\s+/g, "-").toLowerCase()}`}
                          className="text-zinc-400 hover:text-red-600 transition-colors duration-200">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <input value={editBoostInput} onChange={(e) => setEditBoostInput(e.target.value)} data-testid="edit-boost-custom-input"
                    placeholder="Add an area yourself (e.g. Dagenham)" maxLength={60}
                    className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <button type="button" data-testid="edit-boost-add" disabled={!editBoostInput.trim()}
                    onClick={() => {
                      const v = editBoostInput.trim();
                      if (!v) return;
                      setEditForm((f) => ({ ...f, boost_locations: [...new Set([...(f.boost_locations || []), v])].slice(0, 10) }));
                      setEditBoostInput("");
                    }}
                    className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md text-zinc-700 hover:border-zinc-500 disabled:opacity-50 transition-colors duration-200">
                    Add
                  </button>
                </div>
              </div>
              <label className="flex items-start gap-3 border border-zinc-200 rounded-md p-4 bg-zinc-50 cursor-pointer" data-testid="edit-partner-distribution-row">
                <input type="checkbox" checked={!!editForm.partner_distribution} data-testid="edit-partner-distribution"
                  onChange={(e) => setEditForm({ ...editForm, partner_distribution: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-blue-700 shrink-0" />
                <span>
                  <span className="block text-sm font-semibold text-zinc-900">Distribute this vacancy across HiredToday partner job boards</span>
                  <span className="block text-xs text-zinc-500 mt-1">Allow HiredToday to share this vacancy with approved recruitment partners to increase its visibility. Applications may still be managed through HiredToday.</span>
                  {editJob?.partner_distribution && editJob?.partner_distribution_at && (
                    <span className="block text-[11px] text-zinc-400 mt-1">
                      Enabled {new Date(editJob.partner_distribution_at).toLocaleDateString("en-GB")}{editJob.partner_distribution_by ? ` by ${editJob.partner_distribution_by}` : ""}
                    </span>
                  )}
                </span>
              </label>
              <button type="submit" disabled={saving} data-testid="edit-save"
                className="h-11 px-8 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
